import { useRef, useState } from "react";
// import { Link } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { closeSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { useIntercom } from "react-use-intercom";

import UserNameField from "../../Components/Form Fields/username-field";
import OTPField from "../../Components/Form Fields/otp-field";
import PrimaryButton from "../../Components/Buttons/primary-button";

import { useLoginWith2FACode, useRequestLogin2FACode } from "../../CustomHooks/Mutations";

import { useRedirectContext } from "../../Contexts/RedirectContext";

import {
  dispatchErrorToast,
  dispatchInfoToast,
  dispatchSuccessToast,
  transformUSNumberToCountryCodeFormat,
} from "../../Utils/helpers";
import { debugProps } from "../../Utils/constants";

import { StakeLogo } from "../../Assets/Images";

function BackChevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 4L6 8l4 4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 1.5L3 4v3.5c0 3.5 2.14 6.78 5 7.5 2.86-.72 5-4 5-7.5V4L8 1.5z"
        stroke="#54FF99"
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.75 8l1.5 1.5L10.25 6.5"
        stroke="#54FF99"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Login() {
  const resendOtpRef = useRef(false);

  const { show: showIntercom } = useIntercom();

  const [renderOtpField, setRenderOtpField] = useState(false);

  const formProps = useForm();

  const { onLogin } = useRedirectContext();

  const {
    handleSubmit,
    getValues,
    watch,
    formState: { isValid },
    resetField,
  } = formProps;

  const userName = watch("username");

  const { isLoading: isRequestingLogin2FACode, mutate: RequestLogin2FACode } = useRequestLogin2FACode({
    onSuccess: ({ data }) => {
      setRenderOtpField(true);
      if (resendOtpRef.current) {
        resendOtpRef.current = false;
        dispatchSuccessToast(`New OTP code was sent at ${userName}`, { key: "request2FA" });
      } else {
        resetField("otp");
        dispatchInfoToast(data?.data?.msg, { key: "request2FA" });
      }
    },
    onError: () => {
      dispatchErrorToast("Oops! Something went wrong!", { key: "request2FA" });
      if (resendOtpRef.current) {
        resendOtpRef.current = false;
      }
    },
  });

  const { isLoading: isLoggingInWith2FACode, mutate: LoginWith2FACode } = useLoginWith2FACode({
    onSuccess: (data) => {
      dispatchSuccessToast("Verification success");
      onLogin(data);
    },
    onError: (err) =>
      dispatchErrorToast(err?.response?.data?.error?.msg, {
        key: "loginWith2FA",
      }),
  });

  const isLoading = isRequestingLogin2FACode || isLoggingInWith2FACode;

  const onSubmit = () => {
    closeSnackbar();

    let payload = {
      ...debugProps,
      phoneNumber: transformUSNumberToCountryCodeFormat(userName),
    };

    if (!renderOtpField || resendOtpRef.current) {
      !isLoading && RequestLogin2FACode(payload);
    } else {
      payload.phoneNumberCode = getValues("otp");
      !isLoading && LoginWith2FACode(payload);
    }
  };

  const onFormError = (errors) => {
    if (!renderOtpField) {
      dispatchErrorToast(errors.username.message, { key: "username" });
    } else {
      dispatchErrorToast(errors.otp.message, { key: "otp" });
    }
  };

  const disableSubmitButton = !renderOtpField ? !(watch("username") ?? "") : !isValid;

  return (
    <div
      className={`onboarding-page login justify-content-${
        !renderOtpField ? "center" : "sm-center justify-content-start"
      }`}
    >
      <FormProvider {...formProps}>
        <div className="login-card">
          <form
            className={`form-card${renderOtpField ? " otp-state" : ""}`}
            onSubmit={handleSubmit(onSubmit, onFormError)}
            autoComplete="off"
          >
            {renderOtpField ? (
              <div className="card-header">
                <button className="back-button" onClick={setRenderOtpField.bind(this, false)} type="button" aria-label="Go back">
                  <BackChevron />
                </button>
                <h1 className="otp-title">Phone Number Verification</h1>
                <p className="otp-subtitle">
                  Enter the 6-digit verification code sent to <span className="username-badge">{userName}</span>
                </p>
              </div>
            ) : (
              <div className="card-header">
                <StakeLogo className="login-logo" />
                <h1 className="login-title">Welcome to the Renter Network</h1>
                <p className="login-subtitle">
                  Login here to manage your Stake Account. Searching for a home?{" "}
                  <a className="listings-link" href="https://listings.stake.rent" target="_blank" rel="noreferrer">
                    Start here
                  </a>
                </p>
              </div>
            )}

            <div className={`${!renderOtpField ? "username" : "otp"}-field`}>
              {!renderOtpField ? (
                <>
                  <label htmlFor="username">Phone number</label>
                  <UserNameField disabled={isLoading} />
                </>
              ) : (
                <>
                  <OTPField />
                  <button
                    className="resend-otp-button"
                    onClick={() => {
                      resendOtpRef.current = true;
                      onSubmit();
                    }}
                    type="button"
                  >
                    Didn't receive a text? Resend code
                  </button>
                </>
              )}
            </div>

            <PrimaryButton className="w-100 mb-sm-9 mb-6" type="submit" disabled={disableSubmitButton || isLoading}>
              {!renderOtpField ? "Sign In" : "Next"}
              {isLoading && <CircularProgress />}
            </PrimaryButton>

            {/* {!renderOtpField && (
              <div className="signup-link">
                <span className="signup-prompt">Don't have an account?</span>
                <Link className="signup-action" to="/sign-up">Sign up</Link>
              </div>
            )} */}
          </form>

          {!renderOtpField && (
            <>
              <div className="card-divider" />
              <div className="trust-footer">
                <ShieldCheckIcon />
                <span className="trust-label">256-bit encryption</span>
              </div>
            </>
          )}
        </div>
      </FormProvider>
      {renderOtpField && (
        <button className="help-button" onClick={() => showIntercom()}>
          Trouble logging in? Contact Support
        </button>
      )}
    </div>
  );
}
